import { ICollabParticipant } from "../../../../services/collab/collab-types";
import { IDesignerPresence } from "../../../../services/designer-live/designer-live-types";
import { DesignerPresenceReporter } from "../../../../services/designer-live/designer-presence";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import "./collab-presence.scss";

interface ICollabPresenceProps {}

interface IPresenceAvatar {
  key: string;
  userName: string;
  color: string;
  /** Where they are — "UI Design: OrdersScreen"; empty when the roster doesn't know. */
  meta: string;
  sharesMyEntity: boolean;
}

const initialsOf = (userName: string): string => {
  const trimmed: string = (userName || "?").trim();
  const parts: string[] = trimmed.split(/[\s.@_-]+/).filter((p: string) => p.length > 0);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
};

/** DashboardState → human tool name, for the "Ali — Datasources: OrdersDb" rows. */
const toolNameOf = (dashboardState: number): string => {
  switch (dashboardState) {
    case 1:
    case 6:
      return "UI Design";
    case 9:
      return "Datasources";
    case 11:
      return "Process Automation";
    case 12:
      return "Config Manager";
    case 13:
      return "Email Builder";
    case 14:
      return "Scheduled Jobs";
    case 15:
      return "API Builder";
    case 16:
      return "Helpdesk";
    case 17:
      return "Report Designer";
    case 18:
      return "Run Settings";
    case 20:
      return "Branching";
    case 21:
      return "AI Functions";
    default:
      return "Designer";
  }
};

const fromDesignerRoster = (roster: IDesignerPresence[]): IPresenceAvatar[] => {
  const ownClientId: string | null = DesignerPresenceReporter.getClientId();
  const myEntityId: string | undefined = DesignerPresenceReporter.getCurrentEntityId();
  return roster
    .filter((p: IDesignerPresence) => p.clientId !== ownClientId)
    .map((p: IDesignerPresence) => {
      const tool: string = toolNameOf(p.dashboardState);
      const entity: string = p.entityName ? `: ${p.entityName}` : "";
      return {
        key: p.clientId,
        userName: p.userName,
        color: p.color,
        meta: `${tool}${entity}`,
        sharesMyEntity: Boolean(myEntityId && p.entityId === myEntityId)
      };
    });
};

const fromScreenRoster = (roster: ICollabParticipant[]): IPresenceAvatar[] => {
  return roster.map((p: ICollabParticipant) => ({
    key: p.clientId,
    userName: p.userName,
    color: p.color,
    // The entity-session roster only knows "same session as you" — no tool/entity detail.
    meta: "",
    sharesMyEntity: false
  }));
};

/**
 * Live presence (Figma header-toolbar redesign): the trigger is a SINGLE avatar — the current
 * user (--dark-6 disc, per the design) — and the full roster lives in a hover-opened, list-only
 * dropdown: one row per collaborator with their colored avatar, name, and the tool (+ open
 * entity) they're in. No row actions. A warning ring still marks people who have the SAME
 * record open as you (stale-write early warning). Hidden entirely while no roster has anyone,
 * so the toolbar is untouched flag-off — same visibility rule as the old avatar row.
 */
const CollabPresence: React.FC<ICollabPresenceProps & IWithSDProps & IWithContextProp> = ({ sd, context }) => {
  const sessionRoster: ICollabParticipant[] = sd.collabRoster.length > 0 ? sd.collabRoster : sd.workflowCollabRoster;
  const others: IPresenceAvatar[] =
    sd.designerRoster.length > 0 ? fromDesignerRoster(sd.designerRoster) : fromScreenRoster(sessionRoster);
  if (others.length === 0) {
    return null;
  }

  const myName: string = context?.userInfo?.username ?? "";

  const roster: JSX.Element = (
    <div className="kuika-collab-presence__list">
      {others.map((person: IPresenceAvatar) => (
        <div
          className={`kuika-collab-presence__row ${OVERFLOW_REVEAL_HOST_CLASS}`}
          key={person.key}
          data-testid="collabPresenceAvatar"
        >
          <div
            className={`kuika-collab-presence__avatar${person.sharesMyEntity ? " kuika-collab-presence__avatar--shared" : ""}`}
            style={{ backgroundColor: person.color }}
          >
            {initialsOf(person.userName)}
          </div>
          <div className="kuika-collab-presence__row-text">
            <span className="kuika-collab-presence__row-name">
              <KuikaOverflowText>{person.userName}</KuikaOverflowText>
            </span>
            {person.meta.length > 0 && (
              <span className="kuika-collab-presence__row-meta">
                <KuikaOverflowText>{person.meta}</KuikaOverflowText>
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <KuikaDropdownMenu
      options={[]}
      trigger="hover"
      placement="bottomLeft"
      overlayClassName="kuika-collab-presence__dropdown"
      header="In this project"
      emptyState={roster}
      emptyStateCondition
    >
      <div className="kuika-collab-presence" data-testid="collabPresence">
        {/* The trigger is YOU (the neutral --dark-6 disc from the design); everyone else is a
            hover away. aria describes the crowd the visual no longer enumerates. */}
        <div
          className="kuika-collab-presence__avatar kuika-collab-presence__avatar--self"
          data-testid="collabPresenceSelf"
          aria-label={`${others.length} other ${others.length === 1 ? "person" : "people"} in this project`}
        >
          {initialsOf(myName)}
        </div>
      </div>
    </KuikaDropdownMenu>
  );
};

const collabPresence = withContext<ICollabPresenceProps>(withSD<ICollabPresenceProps>(CollabPresence));
export { collabPresence as CollabPresence };
